import { useState, useRef, useEffect } from 'react'
import { useEditorStore } from '../../store/editor.store'
import { BlockRenderer } from '../blocks/renderers'
import { getAllBlocks } from '@bst/blocks'
import type { SectionState } from '../../store/editor.store'

const GLOBAL_SIDS = { header: '__global_header__', footer: '__global_footer__' } as const

interface Props {
  target: 'header' | 'footer'
  section: SectionState
}

export function GlobalSection({ target, section }: Props) {
  const { selectedBlockId, selectBlock, addBlock, removeBlock, toggleGlobalHeader, toggleGlobalFooter, updateGlobalSection } =
    useEditorStore()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [search, setSearch] = useState('')
  const pickerRef = useRef<HTMLDivElement>(null)
  const sectionId = GLOBAL_SIDS[target]
  const label = target === 'header' ? 'Глобальный заголовок' : 'Глобальный футер'
  const toggleFn = target === 'header' ? toggleGlobalHeader : toggleGlobalFooter

  // Close picker on outside click
  useEffect(() => {
    if (!pickerOpen) return
    function handler(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [pickerOpen])

  const allBlocks = getAllBlocks()
  const filtered = search
    ? allBlocks.filter(b => b.label.toLowerCase().includes(search.toLowerCase()) || b.type.includes(search.toLowerCase()))
    : allBlocks

  function handleAddBlock(type: string) {
    const def = allBlocks.find(b => b.type === type)
    if (!def) return
    addBlock(sectionId, type, def.defaultData)
    setPickerOpen(false)
    setSearch('')
  }

  return (
    <div style={{ position: 'relative', margin: '0 0 2px' }}>
      {/* Section badge bar */}
      <div
        style={{
          background: 'linear-gradient(135deg,#7c3aed,#5b6af0)',
          color: '#fff',
          padding: '5px 12px',
          fontSize: '11px',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '13px' }}>{target === 'header' ? '⬆' : '⬇'}</span>
          {label}
          <span style={{ fontWeight: 400, opacity: 0.7, textTransform: 'none' }}>
            · {section.blocks.length} блок{section.blocks.length === 1 ? '' : section.blocks.length < 5 ? 'а' : 'ов'}
          </span>
        </span>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {/* Background picker */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '10px', opacity: 0.85 }} title="Фон секции">
            <input
              type="color"
              value={section.background === 'transparent' ? '#ffffff' : section.background}
              onChange={e => updateGlobalSection(target, { background: e.target.value })}
              style={{ width: '16px', height: '16px', border: 'none', borderRadius: '2px', cursor: 'pointer', padding: 0 }}
              onClick={e => e.stopPropagation()}
            />
          </label>
          <button
            onClick={(e) => { e.stopPropagation(); setPickerOpen(o => !o) }}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '4px', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '3px 8px', cursor: 'pointer' }}
          >
            + Блок
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); toggleFn() }}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '4px', color: '#fff', fontSize: '12px', padding: '3px 7px', cursor: 'pointer', lineHeight: 1 }}
            title="Убрать глобальный блок"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Block picker dropdown */}
      {pickerOpen && (
        <div
          ref={pickerRef}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            width: '260px',
            background: 'var(--bg-1)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            zIndex: 1000,
            overflow: 'hidden',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ padding: '8px' }}>
            <input
              autoFocus
              type="text"
              placeholder="Поиск блоков..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px', background: 'var(--bg-2)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ maxHeight: '280px', overflowY: 'auto', padding: '4px 8px 8px' }}>
            {filtered.map(def => (
              <button
                key={def.type}
                onClick={() => handleAddBlock(def.type)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '7px 8px',
                  background: 'none',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  color: 'var(--text-primary)',
                  fontSize: '12px',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-2)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              >
                <span style={{ fontSize: '16px', flexShrink: 0, width: '22px', textAlign: 'center' }}>{def.icon}</span>
                <span>{def.label}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
                Блоки не найдены
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section content */}
      <div
        style={{
          background: section.background !== 'transparent' ? section.background : undefined,
          paddingTop: section.paddingTop,
          paddingBottom: section.paddingBottom,
          outline: '1px dashed rgba(124,58,237,0.4)',
          outlineOffset: '-1px',
          minHeight: section.blocks.length === 0 ? '72px' : undefined,
          position: 'relative',
        }}
        onClick={e => { e.stopPropagation(); selectBlock(null) }}
      >
        {section.blocks.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '72px', color: 'rgba(124,58,237,0.5)', fontSize: '12px', fontStyle: 'italic' }}>
            Нажмите «+ Блок» чтобы добавить
          </div>
        ) : (
          section.blocks.map(block => (
            <div
              key={block.id}
              style={{
                outline: selectedBlockId === block.id ? '2px solid #7c3aed' : '2px solid transparent',
                outlineOffset: '-2px',
                cursor: 'pointer',
                position: 'relative',
              }}
              onClick={e => { e.stopPropagation(); selectBlock(block.id) }}
            >
              {selectedBlockId === block.id && (
                <div
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    zIndex: 10,
                    display: 'flex',
                    gap: '4px',
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <button
                    onClick={() => removeBlock(sectionId, block.id)}
                    style={{ background: '#ef4444', border: 'none', borderRadius: '4px', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 6px', cursor: 'pointer' }}
                    title="Удалить блок"
                  >
                    ✕
                  </button>
                </div>
              )}
              <BlockRenderer block={block} selected={selectedBlockId === block.id} />
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ── Toggle buttons shown when global section is disabled ──────────────────────

interface ToggleProps {
  target: 'header' | 'footer'
}

export function GlobalSectionToggle({ target }: ToggleProps) {
  const { toggleGlobalHeader, toggleGlobalFooter } = useEditorStore()
  const label = target === 'header' ? 'Глобальный заголовок' : 'Глобальный футер'
  const toggleFn = target === 'header' ? toggleGlobalHeader : toggleGlobalFooter

  return (
    <div style={{ textAlign: 'center', padding: '6px' }}>
      <button
        onClick={(e) => { e.stopPropagation(); toggleFn() }}
        style={{
          background: 'none',
          border: '1px dashed rgba(124,58,237,0.3)',
          borderRadius: '6px',
          color: 'rgba(124,58,237,0.5)',
          fontSize: '11px',
          padding: '4px 12px',
          cursor: 'pointer',
          width: '100%',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = 'rgba(124,58,237,0.05)'
          e.currentTarget.style.color = '#7c3aed'
          e.currentTarget.style.borderColor = '#7c3aed'
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'none'
          e.currentTarget.style.color = 'rgba(124,58,237,0.5)'
          e.currentTarget.style.borderColor = 'rgba(124,58,237,0.3)'
        }}
      >
        + {label}
      </button>
    </div>
  )
}
